import { useState, useCallback, useEffect } from 'react';
import { apiRequest, ApiError } from '../utils/apiClient';
import { useAuth } from './useAuth';

// ============================================================================
// TYPES
// ============================================================================

export interface AdminUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
  status: string;
  accountType?: string;
  createdAt: string;
  lastLoginAt?: string;
  subscription?: UserSubscription;
  // FAL verification fields (agents only)
  agentId?: string;
  licenseNumber?: string;
  falVerificationStatus?: 'PENDING' | 'APPROVED' | 'REJECTED';
  falRejectionReason?: string;
}

export interface UserSubscription {
  id: string;
  planId: string;
  status: string;
  billingCycle: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  plan?: SubscriptionPlan;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  nameAr?: string;
  slug: string;
  priceMonthly: number;
  priceYearly: number;
  maxListings: number;
  maxSubUsers: number;
  isPopular: boolean;
  allowedListingTypes: string;
}

export interface PaginationMeta {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface AdminStats {
  totalUsers: number;
  activeSubscriptions: number;
  totalRevenue: number;
  newUsersThisMonth: number;
  subscriptionsByPlan: Record<string, number>;
}

export interface AdminUsersResponse {
  data: AdminUser[];
  meta: PaginationMeta;
}

export interface AdminFilters {
  page?: number;
  pageSize?: number;
  subscriptionStatus?: string;
  planType?: string;
  search?: string;
}

// ============================================================================
// HOOK
// ============================================================================

export function useAdmin() {
  const { session } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta>({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const token = session?.token;

  // Fetch admin statistics
  const fetchStats = useCallback(async () => {
    if (!token) return;

    try {
      const response = await apiRequest<AdminStats>('/admin/stats', {
        method: 'GET',
        token
      });
      setStats(response);
    } catch (err) {
      console.error('Failed to fetch admin stats:', err);
      setError(err instanceof ApiError ? err.message : 'Failed to fetch statistics');
    }
  }, [token]);

  // Fetch users with filters
  const fetchUsers = useCallback(async (filters: AdminFilters = {}) => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams();
      if (filters.page) queryParams.set('page', filters.page.toString());
      if (filters.pageSize) queryParams.set('pageSize', filters.pageSize.toString());
      if (filters.subscriptionStatus) queryParams.set('subscriptionStatus', filters.subscriptionStatus);
      if (filters.planType) queryParams.set('planType', filters.planType);
      if (filters.search) queryParams.set('search', filters.search);

      const response = await apiRequest<AdminUsersResponse>(
        `/admin/users?${queryParams.toString()}`,
        { method: 'GET', token }
      );

      setUsers(response.data);
      setPagination(response.meta);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setError(err instanceof ApiError ? err.message : 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch subscription plans
  const fetchPlans = useCallback(async () => {
    if (!token) return;

    try {
      const response = await apiRequest<{ data: SubscriptionPlan[] }>('/subscriptions/plans', {
        method: 'GET',
        token
      });
      setPlans(response.data);
    } catch (err) {
      console.error('Failed to fetch plans:', err);
    }
  }, [token]);

  // Fetch user detail
  const fetchUserDetail = useCallback(async (userId: string): Promise<AdminUser | null> => {
    if (!token) return null;

    try {
      const response = await apiRequest<AdminUser>(`/admin/users/${userId}`, {
        method: 'GET',
        token
      });
      return response;
    } catch (err) {
      console.error('Failed to fetch user detail:', err);
      setError(err instanceof ApiError ? err.message : 'Failed to fetch user details');
      return null;
    }
  }, [token]);

  // Initialize data on mount
  useEffect(() => {
    if (token) {
      fetchStats();
      fetchPlans();
    }
  }, [token, fetchStats, fetchPlans]);

  // Grant subscription to a user
  const grantSubscription = useCallback(async (userId: string, planId: string, billingCycle: string = 'monthly'): Promise<boolean> => {
    if (!token) return false;

    try {
      await apiRequest<{ data: UserSubscription; message: string }>(`/admin/users/${userId}/subscription`, {
        method: 'POST',
        token,
        body: { planId, billingCycle }
      });
      return true;
    } catch (err) {
      console.error('Failed to grant subscription:', err);
      setError(err instanceof ApiError ? err.message : 'Failed to grant subscription');
      return false;
    }
  }, [token]);

  // Update user subscription
  const updateSubscription = useCallback(async (userId: string, data: { planId?: string; billingCycle?: string }): Promise<boolean> => {
    if (!token) return false;

    try {
      await apiRequest<{ data: UserSubscription; message: string }>(`/admin/users/${userId}/subscription`, {
        method: 'PUT',
        token,
        body: data
      });
      return true;
    } catch (err) {
      console.error('Failed to update subscription:', err);
      setError(err instanceof ApiError ? err.message : 'Failed to update subscription');
      return false;
    }
  }, [token]);

  // Cancel user subscription
  const cancelSubscription = useCallback(async (userId: string, reason?: string): Promise<boolean> => {
    if (!token) return false;

    try {
      await apiRequest<{ data: UserSubscription; message: string }>(`/admin/users/${userId}/subscription/cancel`, {
        method: 'POST',
        token,
        body: { reason }
      });
      return true;
    } catch (err) {
      console.error('Failed to cancel subscription:', err);
      setError(err instanceof ApiError ? err.message : 'Failed to cancel subscription');
      return false;
    }
  }, [token]);

  return {
    users,
    setUsers,
    stats,
    plans,
    pagination,
    loading,
    error,
    fetchUsers,
    fetchStats,
    fetchPlans,
    fetchUserDetail,
    grantSubscription,
    updateSubscription,
    cancelSubscription,
    clearError: () => setError(null)
  };
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export function getFullName(user: AdminUser): string {
  const first = user.firstName || '';
  const last = user.lastName || '';
  return `${first} ${last}`.trim() || user.email;
}

export function hasActiveSubscription(user: AdminUser): boolean {
  return user.subscription?.status === 'ACTIVE';
}

export function getSubscriptionPlanName(user: AdminUser): string {
  return user.subscription?.plan?.name || 'No subscription';
}

export function getStatusColor(status: string): string {
  switch (status) {
    case 'ACTIVE':
      return 'bg-green-100 text-green-800';
    case 'CANCELLED':
      return 'bg-amber-100 text-amber-800';
    case 'EXPIRED':
      return 'bg-red-100 text-red-800';
    case 'PAST_DUE':
      return 'bg-orange-100 text-orange-800';
    case 'PENDING':
      return 'bg-blue-100 text-blue-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}
